/**
 * Cálculos compartidos de Informes (Por Categoría / Por Mes / Por Año).
 * Todo se calcula en cliente a partir de /api/gastos/todos, sumando
 * cantidad_total igual que los endpoints /por-mes, /por-ano y /por-categoria.
 */

export const SIN_CATEGORIA = 'Sin categoría';
export const OTRAS = 'Otras';

/** Paleta categórica en orden fijo (validada CVD); la 8ª ranura es para "Otras". */
export const PALETA_CATEGORIAS = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#6250d6'
];
export const COLOR_OTRAS = '#9aa39c';

export const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const DIAS_SEMANA = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

export function importe(g) {
  const n = parseFloat(g?.cantidad_total);
  return Number.isFinite(n) ? n : 0;
}

export function diaISO(g) {
  return g?.fecha ? String(g.fecha).split('T')[0] : '';
}

export function categoriaDe(g) {
  return g?.categoria_nombre || SIN_CATEGORIA;
}

export function sumar(gastos) {
  return gastos.reduce((s, g) => s + importe(g), 0);
}

const fmtEuro = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtEuroRedondo = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

export function euros(n, { redondo = false } = {}) {
  return (redondo ? fmtEuroRedondo : fmtEuro).format(Number(n) || 0);
}

/** Variación relativa en % (null si no hay base para comparar). */
export function variacion(actual, anterior) {
  if (!anterior) return null;
  return ((actual - anterior) / anterior) * 100;
}

export function textoVariacion(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  const signo = v > 0 ? '+' : '';
  return `${signo}${v.toFixed(Math.abs(v) < 10 ? 1 : 0)}%`;
}

/** Agrupa sumando importes por la clave que devuelva fnClave. */
export function agruparPor(gastos, fnClave) {
  const mapa = new Map();
  for (const g of gastos) {
    const k = fnClave(g);
    if (!k) continue;
    const prev = mapa.get(k) || { clave: k, total: 0, num: 0 };
    prev.total += importe(g);
    prev.num += 1;
    mapa.set(k, prev);
  }
  return mapa;
}

export const claveMes = (g) => diaISO(g).slice(0, 7);
export const claveAno = (g) => diaISO(g).slice(0, 4);

/**
 * Colores estables por categoría: el ranking histórico (todas las fechas)
 * decide el color, así un filtro nunca repinta una categoría.
 */
export function mapaColoresCategorias(gastos) {
  const ranking = [...agruparPor(gastos, categoriaDe).values()].sort((a, b) => b.total - a.total);
  const mapa = {};
  ranking.forEach((c, i) => {
    mapa[c.clave] = i < PALETA_CATEGORIAS.length ? PALETA_CATEGORIAS[i] : COLOR_OTRAS;
  });
  return mapa;
}

/** Ranking de categorías de un conjunto de gastos, con % sobre el total. */
export function rankingCategorias(gastos) {
  const total = sumar(gastos);
  return [...agruparPor(gastos, categoriaDe).values()]
    .sort((a, b) => b.total - a.total)
    .map((c) => ({
      categoria: c.clave,
      total: c.total,
      num: c.num,
      pct: total > 0 ? (c.total / total) * 100 : 0
    }));
}

export function parseISO(iso) {
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  return new Date(y, (m || 1) - 1, d || 1);
}

export function aISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function diasEntre(desdeISO, hastaISO) {
  return Math.round((parseISO(hastaISO) - parseISO(desdeISO)) / 86400000) + 1;
}

export function diasDelMes(mesISO) {
  const [y, m] = mesISO.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m, 0).getDate();
}

export function mesAnterior(mesISO, n = 1) {
  const [y, m] = mesISO.split('-').map((x) => parseInt(x, 10));
  const d = new Date(y, m - 1 - n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function nombreMes(mesISO, { corto = false, conAno = true } = {}) {
  const [y, m] = mesISO.split('-');
  const nombre = (corto ? MESES_CORTOS : MESES_LARGOS)[parseInt(m, 10) - 1] || '';
  return conAno ? `${nombre} ${corto ? `'${y.slice(2)}` : y}` : nombre;
}

/** Índice de día de la semana con el lunes = 0. */
export function diaSemanaLunes(iso) {
  return (parseISO(iso).getDay() + 6) % 7;
}

/**
 * Periodo anterior de la misma longitud que [desde, hasta]
 * (si no hay rango cerrado, devuelve null).
 */
export function periodoAnterior(desde, hasta) {
  if (!desde || !hasta) return null;
  const dias = diasEntre(desde, hasta);
  const finAnt = parseISO(desde);
  finAnt.setDate(finAnt.getDate() - 1);
  const iniAnt = new Date(finAnt);
  iniAnt.setDate(iniAnt.getDate() - (dias - 1));
  return { desde: aISO(iniAnt), hasta: aISO(finAnt) };
}

/** Serie de N meses (ascendente) que acaba en mesFinal, con totales. */
export function serieMensual(gastos, mesFinal, n) {
  const mapa = agruparPor(gastos, claveMes);
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const mes = mesAnterior(mesFinal, i);
    out.push({ mes, total: mapa.get(mes)?.total || 0 });
  }
  return out;
}

/** Acumulado día a día de un mes (array de longitud = días del mes). */
export function acumuladoDiarioMes(gastos, mesISO) {
  const dias = diasDelMes(mesISO);
  const porDia = new Array(dias).fill(0);
  for (const g of gastos) {
    const d = diaISO(g);
    if (d.slice(0, 7) !== mesISO) continue;
    const idx = parseInt(d.slice(8, 10), 10) - 1;
    if (idx >= 0 && idx < dias) porDia[idx] += importe(g);
  }
  let acc = 0;
  return porDia.map((v) => (acc += v));
}

/** Acumulado mes a mes de un año (12 valores). */
export function acumuladoMensualAno(gastos, ano) {
  const porMes = new Array(12).fill(0);
  for (const g of gastos) {
    const d = diaISO(g);
    if (d.slice(0, 4) !== String(ano)) continue;
    const idx = parseInt(d.slice(5, 7), 10) - 1;
    if (idx >= 0 && idx < 12) porMes[idx] += importe(g);
  }
  let acc = 0;
  return { porMes, acumulado: porMes.map((v) => (acc += v)) };
}

/** Totales por día (YYYY-MM-DD → total). */
export function totalesPorDia(gastos) {
  const mapa = {};
  for (const g of gastos) {
    const d = diaISO(g);
    if (!d) continue;
    mapa[d] = (mapa[d] || 0) + importe(g);
  }
  return mapa;
}

/** Conceptos que más pesan (agrupando por texto normalizado). */
export function topConceptos(gastos, n = 5) {
  const mapa = new Map();
  for (const g of gastos) {
    const bruto = (g.concepto || '').trim();
    if (!bruto) continue;
    const k = bruto.toLowerCase();
    const prev = mapa.get(k) || { concepto: bruto, total: 0, num: 0 };
    prev.total += importe(g);
    prev.num += 1;
    mapa.set(k, prev);
  }
  return [...mapa.values()].sort((a, b) => b.total - a.total).slice(0, n);
}

/** Gasto total por día de la semana (lunes primero). */
export function perfilSemanal(gastos) {
  const totales = new Array(7).fill(0);
  for (const g of gastos) {
    const d = diaISO(g);
    if (!d) continue;
    totales[diaSemanaLunes(d)] += importe(g);
  }
  return totales;
}

/** Nivel 0–4 para mapas de calor (0 = sin gasto) con escala por cuantiles suaves. */
export function nivelCalor(valor, maximo) {
  if (!valor || valor <= 0 || !maximo) return 0;
  const r = Math.sqrt(valor / maximo);
  if (r > 0.75) return 4;
  if (r > 0.5) return 3;
  if (r > 0.25) return 2;
  return 1;
}

/**
 * Nivel 1–4 repartido linealmente entre el mínimo y el máximo (para series
 * homogéneas, como totales mensuales, donde la escala desde 0 lo satura todo).
 */
export function nivelCalorRango(valor, minimo, maximo) {
  if (!valor || valor <= 0) return 0;
  if (maximo <= minimo) return 2;
  const r = (valor - minimo) / (maximo - minimo);
  return Math.min(4, Math.floor(r * 4) + 1);
}

/** Escala secuencial (un solo tono, claro → oscuro) para los mapas de calor. */
export const ESCALA_CALOR = ['#eef1ec', '#c9e3d2', '#8cc3a0', '#3f8f63', '#1a5a3a'];

/** Puntos de una sparkline SVG (viewBox 0 0 w h). */
export function puntosSparkline(valores, w = 100, h = 28, pad = 2) {
  if (!valores.length) return '';
  const max = Math.max(...valores, 0);
  const min = Math.min(...valores, 0);
  const rango = max - min || 1;
  const paso = valores.length > 1 ? (w - pad * 2) / (valores.length - 1) : 0;
  return valores
    .map((v, i) => `${(pad + i * paso).toFixed(1)},${(h - pad - ((v - min) / rango) * (h - pad * 2)).toFixed(1)}`)
    .join(' ');
}
